import { Button, cn } from '@heroui/react'
import { MediaType } from '@qse/api'
import { useIsMobile } from '@qse/utils'
import { Form, Formik, FormikHelpers } from 'formik'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import CreatedAt from '../created-at'
import Dropdown from '../dropdown'
import FormikControl from '../formik-input'

interface SearchFilterFormValues {
  search: string
  fileType?: string
  createdFrom?: string
  createdTo?: string
}

interface DropdownOption {
  label: string
  mimeType?: string
  mediaType?: MediaType
  isDir?: boolean
  onSelect: () => void
  separator?: boolean
  icon?: React.ReactNode
}

interface SearchFilterProps {
  onSearch?: (values: SearchFilterFormValues) => void
  onFileTypeSelect?: (option: DropdownOption) => void
  navbar?: boolean
  redirectToSearchResults?: boolean // NEW PROP
  isInView?: boolean
}

const SearchFilter: React.FC<SearchFilterProps> = ({
  onSearch,
  onFileTypeSelect,
  navbar,
  redirectToSearchResults = false,
  isInView = false,
}) => {
  const navigate = useNavigate()
  const location = useLocation()

  // Add character limit constant
  const MAX_SEARCH_LENGTH = 255

  // Get initial values from URL parameters
  const getInitialSearchValue = () => {
    const searchParams = new URLSearchParams(location.search)

    return searchParams.get('query') || ''
  }

  const getInitialFileType = () => {
    const searchParams = new URLSearchParams(location.search)

    return searchParams.get('fileType') || ''
  }

  const getInitialIsDir = () => {
    const searchParams = new URLSearchParams(location.search)

    return searchParams.get('isDir') === 'true'
  }

  const getInitialDateRange = () => {
    const searchParams = new URLSearchParams(location.search)

    return {
      createdFrom: searchParams.get('createdFrom') || undefined,
      createdTo: searchParams.get('createdTo') || undefined,
    }
  }

  const initialValues: SearchFilterFormValues = {
    search: getInitialSearchValue(),
  }

  const [selectedFileType, setSelectedFileType] = useState<string>(getInitialFileType())
  const [isDir, setIsDir] = useState<boolean>(getInitialIsDir())
  const isMobile = useIsMobile()
  const [dateRange, setDateRange] = useState<{ createdFrom?: string; createdTo?: string }>(
    getInitialDateRange(),
  )
  const [resetTrigger, setResetTrigger] = useState<number>(0)

  // Reset state when location changes
  useEffect(() => {
    const currentFileType = getInitialFileType()
    const currentIsDir = getInitialIsDir()
    const currentDateRange = getInitialDateRange()

    // Reset all filter states when navigating to a new page
    setSelectedFileType(currentFileType)
    setIsDir(currentIsDir)
    setDateRange(currentDateRange)
  }, [location.pathname, location.search])

  const handleClearSearch = (setFieldValue: (field: string, value: any) => void) => {
    setFieldValue('search', '')

    // Trigger search with empty search value
    const searchValues = {
      search: '',
      fileType: isDir ? undefined : selectedFileType,
      isDir: isDir ? true : undefined,
      createdFrom: dateRange.createdFrom,
      createdTo: dateRange.createdTo,
    }

    if (onSearch) {
      onSearch(searchValues)
    }

    // Update URL
    const searchParams = new URLSearchParams(location.search)

    searchParams.delete('query')
    const newSearch = searchParams.toString()
    const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname

    navigate(newUrl, { replace: true })
  }

  const handleInputChange = (value: string, setFieldValue: (field: string, value: any) => void) => {
    // Truncate input if it exceeds the maximum length
    const truncatedValue = value.slice(0, MAX_SEARCH_LENGTH)

    setFieldValue('search', truncatedValue)

    // If the value was truncated, trigger search with truncated value
    if (value.length > MAX_SEARCH_LENGTH && onSearch) {
      const searchValues = {
        search: truncatedValue,
        fileType: isDir ? undefined : selectedFileType,
        isDir: isDir ? true : undefined,
        createdFrom: dateRange.createdFrom,
        createdTo: dateRange.createdTo,
      }

      onSearch(searchValues)
    }
  }

  const handleSubmit = (
    values: SearchFilterFormValues,
    { setSubmitting }: FormikHelpers<SearchFilterFormValues>,
  ) => {
    // Ensure search value doesn't exceed limit
    const truncatedSearch = values.search ? values.search.slice(0, MAX_SEARCH_LENGTH) : ''

    // Combine search values with filters
    const searchValues = {
      ...values,
      search: truncatedSearch,
      fileType: isDir ? undefined : selectedFileType,
      isDir: isDir ? true : undefined,
      createdFrom: dateRange.createdFrom,
      createdTo: dateRange.createdTo,
    }

    if (onSearch) {
      onSearch(searchValues)
    }

    // Update URL
    const searchParams = new URLSearchParams()

    if (values.search) searchParams.set('query', values.search)
    if (isDir) searchParams.set('isDir', 'true')
    else if (selectedFileType) searchParams.set('fileType', selectedFileType)
    if (dateRange.createdFrom) searchParams.set('createdFrom', dateRange.createdFrom)
    if (dateRange.createdTo) searchParams.set('createdTo', dateRange.createdTo)
    const newSearch = searchParams.toString()
    const newUrl = newSearch
      ? `/dashboard/search-results?${newSearch}`
      : '/dashboard/search-results'

    if (redirectToSearchResults) {
      navigate(newUrl, { replace: true })
    } else {
      // Update current URL with search query and filters
      const searchParams = new URLSearchParams(location.search)

      if (values.search) {
        searchParams.set('query', values.search)
      } else {
        searchParams.delete('query')
      }
      if (isDir) {
        searchParams.set('isDir', 'true')
        searchParams.delete('fileType')
      } else if (selectedFileType) {
        searchParams.set('fileType', selectedFileType)
        searchParams.delete('isDir')
      } else {
        searchParams.delete('fileType')
        searchParams.delete('isDir')
      }
      if (dateRange.createdFrom) {
        searchParams.set('createdFrom', dateRange.createdFrom)
      } else {
        searchParams.delete('createdFrom')
      }
      if (dateRange.createdTo) {
        searchParams.set('createdTo', dateRange.createdTo)
      } else {
        searchParams.delete('createdTo')
      }
      const newSearch = searchParams.toString()
      const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname

      navigate(newUrl, { replace: true })
    }
    setSubmitting(false)
  }

  const handleFileTypeSelect = (option: DropdownOption) => {
    if (option.isDir) {
      setIsDir(true)
      setSelectedFileType('')
    } else {
      setIsDir(false)
      setSelectedFileType(option.mimeType || option.mediaType || '')
    }
    onFileTypeSelect?.(option)

    // Immediately trigger search with new file type or isDir
    const currentSearchValue = getInitialSearchValue()
    const truncatedSearchValue = currentSearchValue
      ? currentSearchValue.slice(0, MAX_SEARCH_LENGTH)
      : ''
    const searchValues = {
      search: truncatedSearchValue,
      fileType: option.isDir ? undefined : option.mimeType || option.mediaType || '',
      isDir: option.isDir ? true : undefined,
      createdFrom: dateRange.createdFrom,
      createdTo: dateRange.createdTo,
    }

    if (onSearch) {
      onSearch(searchValues)
    }
    // Update URL
    const searchParams = new URLSearchParams()

    if (currentSearchValue) searchParams.set('query', currentSearchValue)
    if (option.isDir) {
      searchParams.set('isDir', 'true')
    } else if (option.mimeType || option.mediaType)
      searchParams.set('fileType', option.mimeType || option.mediaType || '')
    if (dateRange.createdFrom) searchParams.set('createdFrom', dateRange.createdFrom)
    if (dateRange.createdTo) searchParams.set('createdTo', dateRange.createdTo)
    const newSearch = searchParams.toString()
    const newUrl = newSearch
      ? `/dashboard/search-results?${newSearch}`
      : '/dashboard/search-results'

    if (redirectToSearchResults) {
      navigate(newUrl, { replace: true })
    } else {
      // Update current URL with new file type or isDir
      const searchParams = new URLSearchParams(location.search)

      if (option.isDir) {
        searchParams.set('isDir', 'true')
        searchParams.delete('fileType')
      } else if (option.mimeType || option.mediaType) {
        searchParams.set('fileType', option.mimeType || option.mediaType || '')
        searchParams.delete('isDir')
      } else {
        searchParams.delete('fileType')
        searchParams.delete('isDir')
      }
      const newSearch = searchParams.toString()
      const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname

      navigate(newUrl, { replace: true })
    }
  }

  const handleDateRangeChange = (range: { createdFrom?: string; createdTo?: string }) => {
    setDateRange(range)
    // Immediately trigger search with new date range
    const currentSearchValue = getInitialSearchValue()
    const truncatedSearchValue = currentSearchValue
      ? currentSearchValue.slice(0, MAX_SEARCH_LENGTH)
      : ''
    const searchValues = {
      search: truncatedSearchValue,
      fileType: isDir ? undefined : selectedFileType,
      isDir: isDir ? true : undefined,
      createdFrom: range.createdFrom,
      createdTo: range.createdTo,
    }

    if (onSearch) {
      onSearch(searchValues)
    }
    // Update URL
    const searchParams = new URLSearchParams()

    if (currentSearchValue) searchParams.set('query', currentSearchValue)
    if (isDir) searchParams.set('isDir', 'true')
    else if (selectedFileType) searchParams.set('fileType', selectedFileType)
    if (range.createdFrom) searchParams.set('createdFrom', range.createdFrom)
    if (range.createdTo) searchParams.set('createdTo', range.createdTo)
    const newSearch = searchParams.toString()
    const newUrl = newSearch
      ? `/dashboard/search-results?${newSearch}`
      : '/dashboard/search-results'

    if (redirectToSearchResults) {
      navigate(newUrl, { replace: true })
    } else {
      // Update URL with new date range
      const searchParams = new URLSearchParams(location.search)

      if (range.createdFrom) {
        searchParams.set('createdFrom', range.createdFrom)
      } else {
        searchParams.delete('createdFrom')
      }
      if (range.createdTo) {
        searchParams.set('createdTo', range.createdTo)
      } else {
        searchParams.delete('createdTo')
      }
      const newSearch = searchParams.toString()
      const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname

      navigate(newUrl, { replace: true })
    }
  }

  const handleClearFileTypeFilter = () => {
    setIsDir(false)
    setSelectedFileType('')

    // Trigger search with cleared file type filter
    const currentSearchValue = getInitialSearchValue()
    const truncatedSearchValue = currentSearchValue
      ? currentSearchValue.slice(0, MAX_SEARCH_LENGTH)
      : ''
    const searchValues = {
      search: truncatedSearchValue,
      fileType: undefined,
      isDir: undefined,
      createdFrom: dateRange.createdFrom,
      createdTo: dateRange.createdTo,
    }

    if (onSearch) {
      onSearch(searchValues)
    }

    // Update URL - remove file type and isDir parameters
    const searchParams = new URLSearchParams(location.search)

    searchParams.delete('fileType')
    searchParams.delete('isDir')
    const newSearch = searchParams.toString()
    const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname

    navigate(newUrl, { replace: true })
  }

  const handleClearFilters = () => {
    // Reset all filter states
    setSelectedFileType('')
    setIsDir(false)
    setDateRange({})
    setResetTrigger(prev => prev + 1) // Trigger reset for CreatedAt component

    // Clear search input by triggering form reset
    const searchValues = {
      search: '',
      fileType: undefined,
      isDir: undefined,
      createdFrom: undefined,
      createdTo: undefined,
    }

    if (onSearch) {
      onSearch(searchValues)
    }

    // Clear all URL parameters
    navigate(location.pathname, { replace: true })
  }

  // Check if any filters are active
  const hasActiveFilters =
    isDir ||
    selectedFileType ||
    dateRange.createdFrom ||
    dateRange.createdTo ||
    (getInitialSearchValue() && getInitialSearchValue().length > 0)

  const dropdownOptions: DropdownOption[] = [
    {
      label: 'Folder',
      isDir: true,
      icon: <img alt="folder" className="w-5 h-5" src="/assets/svg/folder.svg" />,
      onSelect: () => {
        handleFileTypeSelect({
          label: 'Folder',
          isDir: true,
          onSelect: () => {},
        })
      },
      separator: true,
    },
    {
      label: 'Image',
      mediaType: MediaType.IMAGE,
      icon: <img alt="image" className="w-5 h-5" src="/assets/svg/file-types/picture.svg" />,
      onSelect: () => {
        handleFileTypeSelect({
          label: 'Image',
          mediaType: MediaType.IMAGE,
          onSelect: () => {},
        })
      },
    },
    {
      label: 'Video',
      mediaType: MediaType.VIDEO,
      icon: <img alt="video" className="w-5 h-5 " src="/assets/svg/file-types/video.svg" />,
      onSelect: () => {
        handleFileTypeSelect({
          label: 'Video',
          mediaType: MediaType.VIDEO,
          onSelect: () => {},
        })
      },
    },
    {
      label: 'Audio',
      mediaType: MediaType.AUDIO,
      icon: <img alt="audio" className="w-5 h-5" src="/assets/svg/file-types/audio.svg" />,
      onSelect: () => {
        handleFileTypeSelect({
          label: 'Audio',
          mediaType: MediaType.AUDIO,
          onSelect: () => {},
        })
      },
    },
    {
      label: 'Document',
      mediaType: MediaType.DOCUMENT,
      icon: <img alt="document" className="w-5 h-5" src="/assets/svg/file-types/docs.svg" />,
      onSelect: () => {
        handleFileTypeSelect({
          label: 'Document',
          mediaType: MediaType.DOCUMENT,
          onSelect: () => {},
        })
      },
    },
    {
      label: 'Spreadsheet',
      mediaType: MediaType.SPREADSHEET,
      icon: <img alt="spreadsheet" className="w-5 h-5" src="/assets/svg/file-types/excel.svg" />,
      onSelect: () => {
        handleFileTypeSelect({
          label: 'Spreadsheet',
          mediaType: MediaType.SPREADSHEET,
          onSelect: () => {},
        })
      },
    },
    {
      label: 'Presentation',
      mediaType: MediaType.PRESENTATION,
      icon: <img alt="presentation" className="w-5 h-5" src="/assets/svg/file-types/ppt.svg" />,
      onSelect: () => {
        handleFileTypeSelect({
          label: 'Presentation',
          mediaType: MediaType.PRESENTATION,
          onSelect: () => {},
        })
      },
    },
    {
      label: 'PDF',
      mediaType: MediaType.PDF,
      icon: <img alt="pdf" className="w-5 h-5" src="/assets/svg/file-types/pdf.svg" />,
      onSelect: () => {
        handleFileTypeSelect({
          label: 'PDF',
          mediaType: MediaType.PDF,
          onSelect: () => {},
        })
      },
    },
    {
      label: 'Archive',
      mediaType: MediaType.ARCHIVE,
      icon: <img alt="archive" className="w-5 h-5" src="/assets/svg/file-types/archives.svg" />,
      onSelect: () => {
        handleFileTypeSelect({
          label: 'Archive',
          mediaType: MediaType.ARCHIVE,
          onSelect: () => {},
        })
      },
    },
    {
      label: 'Other',
      mediaType: MediaType.OTHER,
      icon: <img alt="other" className="w-5 h-5" src="/assets/svg/file-text.svg" />,
      onSelect: () => {
        handleFileTypeSelect({
          label: 'Other',
          mediaType: MediaType.OTHER,
          onSelect: () => {},
        })
      },
    },
  ]

  // Get the selected file type label for display
  const selectedFileTypeIcon = useMemo(() => {
    if (isDir) return <img alt="folder" className="w-5 h-5" src="/assets/svg/folder.svg" />
    if (selectedFileType) {
      const option = dropdownOptions.find(opt => opt.mediaType === selectedFileType)

      if (option?.icon) return option?.icon
    }

    return filterIcon
  }, [isDir, selectedFileType])

  if (navbar) {
    return (
      <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
        {({ setFieldValue, values }) => (
          <Form className="w-full">
            <div className="relative w-full">
              <FormikControl
                autoComplete="off"
                control="text"
                maxLength={MAX_SEARCH_LENGTH}
                name="search"
                placeholder="Search"
                size={isMobile ? 'md' : 'lg'}
                type="text"
                endContent={
                  <div className="flex items-center gap-2">
                    <div className="text-xs text-gray-400 dark:text-gray-500">
                      {values.search.length}/{MAX_SEARCH_LENGTH}
                    </div>
                    {values.search && values.search.length > 0 && (
                      <button
                        className=" text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
                        title="Clear search"
                        type="button"
                        onClick={() => {
                          handleClearSearch(setFieldValue)
                        }}
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M6 18L18 6M6 6l12 12"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                }
                startContent={
                  <svg
                    className="w-7 h-7"
                    fill="none"
                    height="28"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    width="28"
                  >
                    <path
                      d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                    />
                  </svg>
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  handleInputChange(e.target.value, setFieldValue)
                }}
              />
            </div>

            <div className="flex md:gap-5 gap-2 flex-wrap mt-3 items-center">
              <div className="relative">
                <Dropdown
                  showHighlight
                  className="w-full bg-white dark:bg-[#1E1E1E] text-gray-900 dark:text-gray-300 border border-gray-300 dark:border-gray-600"
                  options={dropdownOptions}
                  startContent={selectedFileTypeIcon}
                  title="All files"
                  endContent={
                    <img
                      alt="calendar"
                      className="filter dark:invert"
                      src="/assets/svg/down-arrow.svg"
                    />
                  }
                  selectedOption={
                    isDir
                      ? 'Folder'
                      : selectedFileType
                        ? String(
                            dropdownOptions.find(opt => opt.mediaType === selectedFileType)
                              ?.label || '',
                          )
                        : ''
                  }
                  onOptionChange={option => {
                    if (typeof option !== 'string') return
                    const selectedOption = dropdownOptions.find(opt => opt.label === option)

                    if (selectedOption) {
                      handleFileTypeSelect(selectedOption)
                    }
                  }}
                />
                {/* Clear button for file type filter */}
                {(isDir || selectedFileType) && (
                  <button
                    className="absolute -right-2 -top-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors z-10"
                    title="Clear file type filter"
                    type="button"
                    onClick={() => {
                      handleClearFileTypeFilter()
                    }}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        d="M6 18L18 6M6 6l12 12"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                      />
                    </svg>
                  </button>
                )}
              </div>
              <CreatedAt resetTrigger={resetTrigger} onDateRangeChange={handleDateRangeChange} />
              {hasActiveFilters && (
                <Button
                  size="sm"
                  title="Clear all filters"
                  type="button"
                  variant="flat"
                  onPress={() => {
                    handleClearFilters()
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </Form>
        )}
      </Formik>
    )
  }

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center px-4 md:px-[30px]  text-gray-900 dark:text-white',
        isInView ? 'opacity-100' : 'opacity-0 select-none pointer-events-none',
      )}
      style={{
        // Prevent layout shifts on mobile by maintaining consistent height
        minHeight: isMobile ? '180px' : 'auto',
        // Fast, smooth transitions for better UX
        transition: isMobile
          ? 'opacity 0.2s ease-out, transform 0.2s ease-out'
          : 'opacity 0.15s ease-out',
        // Ensure the element maintains its space even when hidden
        visibility: isInView ? 'visible' : 'hidden',
      }}
    >
      <div className="flex flex-col gap-2 sm:gap-4 mb-6 sm:mb-8 md:mb-10 lg:mb-[30px] text-center">
        <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-[28px] font-medium text-gray-900 dark:text-white">
          Welcome to Drive
        </h1>
      </div>

      <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
        {({ setFieldValue, values }) => (
          <Form className="flex gap-3 sm:gap-4 flex-col w-full max-w-[604px]">
            <div className="relative w-full">
              <FormikControl
                autoComplete="off"
                classNameContainer="w-full"
                control="text"
                maxLength={MAX_SEARCH_LENGTH}
                name="search"
                placeholder="Search"
                size="lg"
                type="text"
                endContent={
                  <div className="flex items-center gap-2">
                    {values.search && (
                      <div className="text-xs text-gray-400 dark:text-gray-500">
                        {values.search.length}/{MAX_SEARCH_LENGTH}
                      </div>
                    )}
                    {values.search && values.search.length > 0 && (
                      <button
                        className="text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
                        title="Clear search"
                        type="button"
                        onClick={() => {
                          setFieldValue('search', '')
                          // Trigger search with empty search value
                          const searchValues = {
                            search: '',
                            fileType: isDir ? undefined : selectedFileType,
                            isDir: isDir ? true : undefined,
                            createdFrom: dateRange.createdFrom,
                            createdTo: dateRange.createdTo,
                          }

                          if (onSearch) {
                            onSearch(searchValues)
                          }
                          // Update URL
                          const searchParams = new URLSearchParams(location.search)

                          searchParams.delete('query')
                          const newSearch = searchParams.toString()
                          const newUrl = newSearch
                            ? `${location.pathname}?${newSearch}`
                            : location.pathname

                          navigate(newUrl, { replace: true })
                        }}
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M6 18L18 6M6 6l12 12"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  handleInputChange(e.target.value, setFieldValue)
                }}
              />
              {/* Character count indicator for main search form */}
            </div>
            {/* Mobile: Stack vertically, Desktop: Side by side - Hide filters when showing not found error */}
            <div className="flex gap-5 justify-center items-center">
              <div className="relative">
                <Dropdown
                  showHighlight
                  className="w-full bg-white dark:bg-[#1E1E1E] text-gray-900 dark:text-gray-300 border border-gray-300 dark:border-gray-600"
                  options={dropdownOptions}
                  startContent={selectedFileTypeIcon}
                  title="All files"
                  endContent={
                    <img
                      alt="calendar"
                      className="filter dark:invert"
                      src="/assets/svg/down-arrow.svg"
                    />
                  }
                  selectedOption={
                    isDir
                      ? 'Folder'
                      : selectedFileType
                        ? String(
                            dropdownOptions.find(opt => opt.mediaType === selectedFileType)
                              ?.label || '',
                          )
                        : ''
                  }
                  onOptionChange={option => {
                    if (typeof option !== 'string') return
                    const selectedOption = dropdownOptions.find(opt => opt.label === option)

                    if (selectedOption) {
                      handleFileTypeSelect(selectedOption)
                    }
                  }}
                />
                {/* Clear button for file type filter */}
                {(isDir || selectedFileType) && (
                  <button
                    className="absolute -right-2 -top-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors z-10"
                    title="Clear file type filter"
                    type="button"
                    onClick={() => {
                      handleClearFileTypeFilter()
                    }}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        d="M6 18L18 6M6 6l12 12"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                      />
                    </svg>
                  </button>
                )}
              </div>
              <CreatedAt resetTrigger={resetTrigger} onDateRangeChange={handleDateRangeChange} />
              {hasActiveFilters && (
                <button
                  className="px-4 py-2 text-sm text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 rounded-lg"
                  title="Clear all filters"
                  type="button"
                  onClick={() => {
                    handleClearFilters()
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

const filterIcon = (
  <svg
    className="dark:fill-white"
    fill="#444746"
    height="20"
    viewBox="0 -960 960 960"
    width="20"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M240-80q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h320l240 240v480q0 33-23.5 56.5T720-80H240Zm280-520v-200H240v640h480v-440H520ZM240-800v200-200 640-640Z" />
  </svg>
)

export default SearchFilter
